import React, { useEffect, useRef } from 'react'
import { PortfolioStatement } from '../../../services'

type HeaderProps = {
  isStatementsMenuOpen: boolean
  statements: PortfolioStatement[]
  statementsLoading: boolean
  downloadingStatementId: string | null
  onToggleStatements: () => void
  onDownloadStatement: (id: string) => void
  onNewInvestment: () => void
  showSecondaryMarketLink?: boolean
  onSecondaryMarket?: () => void
}

const formatStatementDate = (iso: string) => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString()
}

const statementTitle = (statement: PortfolioStatement) =>
  statement.label?.trim() || statement.originalFilename

const IconDownload = () => (
  <svg viewBox='0 0 20 20' fill='none' aria-hidden focusable='false'>
    <path d='M10 3v9' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' />
    <path
      d='M6.5 8.5 10 12l3.5-3.5'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M4 14.5v1A1.5 1.5 0 0 0 5.5 17h9a1.5 1.5 0 0 0 1.5-1.5v-1'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
    />
  </svg>
)

export const DashboardHeader: React.FC<HeaderProps> = ({
  isStatementsMenuOpen,
  statements,
  statementsLoading,
  downloadingStatementId,
  onToggleStatements,
  onDownloadStatement,
  onNewInvestment,
  showSecondaryMarketLink,
  onSecondaryMarket,
}) => {
  const statementsActionRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!isStatementsMenuOpen) return

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null
      if (statementsActionRef.current && target && !statementsActionRef.current.contains(target)) {
        onToggleStatements()
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('touchstart', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('touchstart', handleOutsideClick)
    }
  }, [isStatementsMenuOpen, onToggleStatements])

  return (
    <section className='dashboard-v2__header'>
      <div>
        <h1>Portfolio Overview</h1>
      </div>
      <div className='dashboard-v2__header-actions'>
        <div className='export-action' ref={statementsActionRef}>
          <button type='button' className='secondary' onClick={onToggleStatements}>
            Portfolio Statements
          </button>
          {isStatementsMenuOpen && (
            <div className='export-dropdown portfolio-statements-dropdown'>
              <p className='portfolio-statements-dropdown__title'>Portfolio Statements</p>
              {statementsLoading ? (
                <p className='portfolio-statements-dropdown__empty'>Loading…</p>
              ) : statements.length === 0 ? (
                <p className='portfolio-statements-dropdown__empty'>No portfolio statements yet.</p>
              ) : (
                <ul className='portfolio-statements-dropdown__list'>
                  {statements.map((statement) => {
                    const isDownloading = downloadingStatementId === statement.id
                    return (
                      <li key={statement.id} className='portfolio-statements-dropdown__item'>
                        <div className='portfolio-statements-dropdown__meta'>
                          <span className='portfolio-statements-dropdown__name'>
                            {statementTitle(statement)}
                          </span>
                          <span className='portfolio-statements-dropdown__date'>
                            {formatStatementDate(statement.createdAt)}
                          </span>
                        </div>
                        <button
                          type='button'
                          className='portfolio-statements-dropdown__download'
                          aria-label={
                            isDownloading
                              ? `Downloading ${statementTitle(statement)}`
                              : `Download ${statementTitle(statement)}`
                          }
                          disabled={isDownloading || Boolean(downloadingStatementId)}
                          onClick={() => onDownloadStatement(statement.id)}
                        >
                          <IconDownload />
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
        <button type='button' onClick={onNewInvestment}>
          New Investment
        </button>
        {showSecondaryMarketLink && onSecondaryMarket ? (
          <button type='button' className='secondary' onClick={onSecondaryMarket}>
            Secondary Market
          </button>
        ) : null}
      </div>
    </section>
  )
}
